import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { companyInfo, getServices } from "../mock";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const Contact = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const services = getServices(language);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    subject: "",
    message: "",
    urgency: "normal"
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert("Merci pour votre message ! Nous vous contacterons dans les plus brefs délais.");
    console.log("Form submitted:", formData);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: "#283747" }}>
            {t('contactPage.title')}
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('contactPage.description')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div>
            <Card className="shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <MessageCircle className="w-6 h-6 mr-3" style={{ color: "#A04030" }} />
                  <h2 className="text-2xl font-bold" style={{ color: "#283747" }}>
                    {t('contactPage.formTitle')}
                  </h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                        {t('contactPage.nameLabel')} *
                      </label>
                      <Input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className="border-2 border-slate-200 focus:border-yellow-500"
                        placeholder={t('contactPage.namePlaceholder')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                        {t('contactPage.phoneLabel')} *
                      </label>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        className="border-2 border-slate-200 focus:border-yellow-500"
                        placeholder={t('contactPage.phonePlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                      {t('contactPage.emailLabel')} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="border-2 border-slate-200 focus:border-yellow-500"
                      placeholder={t('contactPage.emailPlaceholder')}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                        {t('contactPage.serviceLabel')}
                      </label>
                      <Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
                        <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                          <SelectValue placeholder={t('contactPage.servicePlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t('contactPage.generalConsultation')}</SelectItem>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                        {t('contactPage.urgencyLabel')}
                      </label>
                      <Select value={formData.urgency} onValueChange={(value) => handleInputChange("urgency", value)}>
                        <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t('contactPage.urgencyLow')}</SelectItem>
                          <SelectItem value="normal">{t('contactPage.urgencyNormal')}</SelectItem>
                          <SelectItem value="high">{t('contactPage.urgencyHigh')}</SelectItem>
                          <SelectItem value="urgent">{t('contactPage.urgencyUrgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                      {t('contactPage.subjectLabel')}
                    </label>
                    <Input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange("subject", e.target.value)}
                      className="border-2 border-slate-200 focus:border-yellow-500"
                      placeholder={t('contactPage.subjectPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                      {t('contactPage.messageLabel')} *
                    </label>
                    <Textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      className="border-2 border-slate-200 focus:border-yellow-500 resize-none"
                      placeholder={t('contactPage.messagePlaceholder')}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-white font-semibold py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: "#A04030" }}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {t('contactPage.sendButton')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ color: "#283747" }}>
                  {t('contactPage.contactInfo')}
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                      style={{ backgroundColor: "#D4A86A" }}
                    >
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: "#283747" }}>
                        Téléphone
                      </h3>
                      <p className="text-slate-600 mb-2">506-588-4919</p>
                      <p className="text-sm text-slate-500">
                        Mohamed Obad - Disponible pour appels
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                      style={{ backgroundColor: "#D4A86A" }}
                    >
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: "#283747" }}>
                        Email
                      </h3>
                      <p className="text-slate-600 mb-2">aymorenov25@gmail.com</p>
                      <p className="text-sm text-slate-500">
                        Réponse garantie sous 24h
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                      style={{ backgroundColor: "#D4A86A" }}
                    >
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: "#283747" }}>
                        Adresse
                      </h3>
                      <p className="text-slate-600 mb-2">Nouveau-Brunswick, Canada</p>
                      <p className="text-sm text-slate-500">
                        Service disponible dans toute la région
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                      style={{ backgroundColor: "#D4A86A" }}
                    >
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: "#283747" }}>
                        Heures d'ouverture
                      </h3>
                      <p className="text-slate-600 mb-2">{companyInfo.contact.hours}</p>
                      <p className="text-sm text-slate-500">
                        Consultations d'urgence disponibles
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-6" style={{ color: "#283747" }}>
                  Actions Rapides
                </h3>
                
                <div className="space-y-4">
                  <a 
                    href="https://wa.me/5065884919?text=Bonjour Mohamed, j'aimerais discuter de mes besoins en rénovation avec AYMORENOV CONSTRUCTION INC"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      className="w-full text-white font-medium py-3 rounded-lg transition-all duration-300 hover:scale-105"
                      style={{ backgroundColor: "#25D366" }}
                    >
                      💬 WhatsApp - Réponse Immédiate
                    </Button>
                  </a>
                  
                  <Button
                    variant="outline"
                    className="w-full border-2 text-slate-700 hover:text-white py-3 rounded-lg transition-all duration-300"
                    style={{ borderColor: "#D4A86A", "--tw-hover-bg": "#D4A86A" }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#D4A86A"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = ""}
                  >
                    📋 Demande de Devis en Ligne
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full border-2 text-slate-700 hover:text-white py-3 rounded-lg transition-all duration-300"
                    style={{ borderColor: "#D4A86A", "--tw-hover-bg": "#D4A86A" }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#D4A86A"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = ""}
                  >
                    📅 Planifier une Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Notice */}
            <div 
              className="p-6 rounded-xl text-white"
              style={{ backgroundColor: "#A04030" }}
            >
              <h3 className="font-bold text-lg mb-2">🚨 Service d'Urgence</h3>
              <p className="text-sm opacity-90">
                Pour les urgences (dégâts d'eau, problèmes électriques, etc.), 
                appelez-nous immédiatement au {companyInfo.contact.phone}. 
                Un spécialiste se déplacera dans l'heure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;